import React, { useState, useEffect } from 'react';
import { LuChevronRight, LuFileText, LuUsers } from 'react-icons/lu';
import { useHPCStore } from '../../store/hpcStore';
import useSchoolStore from '../../store/schoolStore';
import { getSectionsForClass, getClassName } from '../../utils/schoolUtils';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingMessage from '../../components/common/LoadingMessage';
import PageHeader from '../../components/common/PageHeader';
import Tabs from '../../components/common/Tabs';
import Table, { tdClass } from '../../components/common/Table';
import Avatar from '../../components/common/Avatar';
import InfoGrid, { InfoItem } from '../../components/common/InfoGrid';
import EmptyState from '../../components/common/EmptyState';
import ErrorWidget from '../../components/common/ErrorWidget';
import { Field, Select } from '../../components/common/FormControls';
import HPCReportDetailsView from './components/HPCReportDetailsView';
import HPCReportWindow from '../../components/hpc/HPCReportWindow';

const HPCReportPage = () => {
  const [activeTab, setActiveTab] = useState('hpc');
  const [viewMode, setViewMode] = useState('list'); // 'list', 'details', or 'report'
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [studentFilters, setStudentFilters] = useState({
    Class: null,
    sec: null,
    academicYear: ''
  });
  
  const {
    hpcReports,
    studentHPCReports,
    loading,
    error,
    getHPCReports,
    getHPCStudentReports
  } = useHPCStore();

  const {
    schoolId,
    getAcademicYears,
    academicYears,
    schoolData,
    isClassesLoading,
    classes,
    getClasses
  } = useSchoolStore();

  useEffect(() => {
    // Check if school ID is available
    if (!schoolId) {
      console.warn('School ID not available. Please select a school first.');
      return;
    }

    // Fetch academic years and classes when component mounts
    const fetchData = async () => {
      try {
        await getAcademicYears();
        getClasses(); // Don't wait for classes, let dropdown show loading state
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();

    // Load HPC reports if on HPC tab
    if (activeTab === 'hpc') {
      getHPCReports(schoolId);
    }
  }, [activeTab, schoolId, getHPCReports, getAcademicYears, getClasses]);


  const handleStudentReportsSearch = () => {
    if (!schoolId) {
      console.error('School ID not available');
      return;
    }
    
    if (studentFilters.Class && studentFilters.sec) {
      // Use selected academic year or fall back to current academic year from school data
      const academicYear = studentFilters.academicYear || schoolData?.currentAcademicYear;
      
      if (academicYear) {
        getHPCStudentReports(
          schoolId, 
          studentFilters.Class, 
          studentFilters.sec, 
          academicYear
        );
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Function to show HPC Report Details view
  const handleViewDetails = (reportId) => {
    if (reportId) {
      setSelectedReportId(reportId);
      
      // If it's a student report, open HPCReportWindow
      if (activeTab === 'student') {
        setViewMode('report');
      } else {
        // For HPC reports, show details view
        setViewMode('details');
      }
    } else {
      console.error('Report ID is required to view details');
    }
  };

  // Function to go back to reports list
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedReportId(null);
  };

  if (!schoolId) {
    return <EmptyState title="No school selected" message="Open the dashboard from your school’s link to see its HPC reports." />;
  }

  const renderHPCReports = () => {
    if (loading) {
      return (
        <Card>
          <LoadingMessage message="Loading HPC reports…" />
        </Card>
      );
    }

    if (error) {
      return <ErrorWidget error={{ message: error }} title="Could not load the HPC reports" onRetry={() => getHPCReports(schoolId)} />;
    }

    if (!hpcReports || hpcReports.length === 0) {
      return (
        <Card>
          <EmptyState icon={<LuFileText />} title="No HPC reports yet" message="Holistic Progress Card templates made for the school will appear here." />
        </Card>
      );
    }

    return (
      <Card noPadding>
        <Table head={['Report', 'Classes', 'Sections', '']}>
          {hpcReports.map((report, index) => (
            <tr key={report.id || index} onClick={() => handleViewDetails(report.id)} className="cursor-pointer">
              <td className={`${tdClass} font-medium`}>{report.name || `Report ${index + 1}`}</td>
              <td className={tdClass}>{report.classes ? report.classes.join(', ') : '—'}</td>
              <td className={`${tdClass} tabular-nums`}>{report.sections ? report.sections.length : '—'}</td>
              <td className={`${tdClass} w-32 text-right`}>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleViewDetails(report.id);
                  }}
                  className="inline-flex items-center gap-1 rounded text-[13px] font-medium text-ui-link hover:underline"
                >
                  View details
                  <LuChevronRight aria-hidden className="size-4" />
                </button>
              </td>
            </tr>
          ))}
        </Table>
      </Card>
    );
  };

  const renderStudentResults = () => {
    if (loading) {
      return (
        <Card>
          <LoadingMessage message="Loading student reports…" />
        </Card>
      );
    }

    if (error) {
      return <ErrorWidget error={{ message: error }} title="Could not load the student reports" onRetry={handleStudentReportsSearch} />;
    }

    if (!studentHPCReports || studentHPCReports.length === 0) {
      return (
        <Card>
          <EmptyState
            icon={<LuUsers />}
            title="No student reports to show"
            message="Choose a class and section above, then press “Show reports”."
          />
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {studentHPCReports.map((report, index) => (
          <Card key={report.id || index} bodyClassName="flex h-full flex-col">
            <div className="flex items-center gap-3">
              <Avatar name={report.studentName || 'Student'} src={report.profileImage} size="md" />
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold text-ui-ink">{report.studentName || `Student ${index + 1}`}</h3>
                <p className="text-xs text-ui-ink-3">
                  {report.Class} {report.sec} · {report.academicYear}
                </p>
              </div>
            </div>

            <InfoGrid columns={2} className="mt-4">
              {report.name && <InfoItem label="Report" value={report.name} />}
              {(report.fatherName || report.guardianName) && (
                <InfoItem label={report.fatherName ? 'Father' : 'Guardian'} value={report.fatherName || report.guardianName} />
              )}
              {report.dob && <InfoItem label="Date of birth" value={formatDate(report.dob)} />}
              {report.gender && <InfoItem label="Gender" value={report.gender} className="capitalize" />}
            </InfoGrid>

            {report.overallSummary && (
              <p className="mt-4 line-clamp-3 rounded-lg bg-ui-muted px-3 py-2 text-xs leading-relaxed text-ui-ink-2">
                {report.overallSummary}
              </p>
            )}

            <div className="mt-auto pt-4">
              <Button variant="secondary" size="sm" fullWidth onClick={() => handleViewDetails(report.id)}>
                Open full report
              </Button>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderStudentReports = () => {
    return (
      <>
        <Card className="mb-5">
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
            <Field label="Class">
              <Select
                value={studentFilters.Class || ''}
                onChange={(event) => {
                  const selectedClass = event.target.value;
                  setStudentFilters(previousFilters => ({
                    ...previousFilters,
                    Class: selectedClass,
                    sec: null // Reset section when class changes
                  }));
                }}
                disabled={isClassesLoading}
              >
                <option value="">{isClassesLoading ? 'Loading classes…' : 'Select class'}</option>
                {!isClassesLoading && classes && classes.map((classItem) => (
                  <option key={classItem.Class} value={classItem.Class}>
                    {getClassName(schoolId, classItem.Class)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Section">
              <Select
                value={studentFilters.sec || ''}
                onChange={(event) => setStudentFilters(previousFilters => ({ ...previousFilters, sec: event.target.value }))}
                disabled={isClassesLoading || !studentFilters.Class}
              >
                <option value="">{!studentFilters.Class ? 'Select class first' : 'Select section'}</option>
                {!isClassesLoading && studentFilters.Class && classes &&
                  getSectionsForClass(classes, studentFilters.Class).map((section) => (
                    <option key={section} value={section}>
                      {section}
                    </option>
                  ))
                }
              </Select>
            </Field>
            <Field
              label="Academic year"
              hint={!studentFilters.academicYear && schoolData?.currentAcademicYear ? `Current year (${schoolData.currentAcademicYear}) if left empty` : undefined}
            >
              <Select
                value={studentFilters.academicYear}
                onChange={(event) => setStudentFilters(previousFilters => ({ ...previousFilters, academicYear: event.target.value }))}
              >
                <option value="">Select academic year</option>
                {academicYears && academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              onClick={handleStudentReportsSearch}
              disabled={!studentFilters.Class || !studentFilters.sec}
              className={!studentFilters.academicYear && schoolData?.currentAcademicYear ? 'sm:mb-[22px]' : ''}
            >
              Show reports
            </Button>
          </div>
        </Card>

        {renderStudentResults()}
      </>
    );
  };

  if (viewMode === 'details') {
    return <HPCReportDetailsView reportId={selectedReportId} onBack={handleBackToList} />;
  }

  if (viewMode === 'report') {
    return <HPCReportWindow reportId={selectedReportId} onBack={handleBackToList} />;
  }

  const tabs = [
    { id: 'hpc', label: 'Report templates', icon: LuFileText },
    { id: 'student', label: 'Student reports', icon: LuUsers },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="HPC reports" subtitle="Holistic Progress Cards: the report templates and each student’s card." />

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={(tabId) => {
          setActiveTab(tabId);
          handleBackToList(); // Reset to list view when switching tabs
        }}
        className="mb-5"
      />

      {activeTab === 'hpc' ? renderHPCReports() : renderStudentReports()}
    </div>
  );
};

export default HPCReportPage;
